// React hook that fetches the per-boat device inventory from
// `GET /v1/boats/:boatId/devices` (task device_discovery). Shape mirrors the cloud
// `vessel_devices` response — see cloud/src/api/devices/routes.ts.
//
// Subscribes to the user-WS `boat.<id>.devices` topic and refetches
// whenever cloud signals `inventory_changed` (device_inventory_digest
// accepted) or `liveness_changed` (device_liveness accepted). The
// notification carries no payload data; we just re-run the GET to pick
// up the latest snapshot. Falls back to a passive view if the WS is
// unavailable (cookie missing, gateway down, etc.) — the manual
// `refresh` returned by the hook still works.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { beforeWsReconnect } from './wsReconnect';

// An admin-published manual for a device's shared model (ADR 0112). Mirrors the
// slim owner-facing shape from cloud/src/api/devices/routes.ts — review metadata
// (source, confidence, status, …) is intentionally not exposed to owners.
export interface UserManual {
  id: string;
  title: string;
  revision: string | null;
  language: string | null;
  sizeBytes: number | null;
  // Authenticated owner proxy route; open directly (the session cookie rides
  // along on the same-origin GET).
  downloadUrl: string;
}

export interface VesselDevice {
  deviceId: string;
  protocol: 'n2k' | 'n0183' | 'sk' | 'venus' | 'starlink';
  // Resolved display name (ADR 0052): user preferred_name → device_label →
  // admin preferred_name → inferred `model_version (function)`. Always set.
  displayName: string;
  // The per-unit user override alone (null today — no user edit path yet).
  preferredName: string | null;
  sourceId: string | null;
  // Best-effort gateway/feed name resolved from the boat's latest source
  // analysis. The stable sourceId remains available for diagnostics.
  sourceName?: string;
  srcAddress?: number;
  canName?: string;
  // Protocol-independent bus identity (`n2kphys:<mfrCode>:<uniqueNumber>`). Two
  // rows that share it are the SAME physical device seen through different
  // protocols — e.g. the native N2K view and the Signal K view of one SIM100.
  // Those rows are deliberately NOT merged (see core `wireDeviceId`): each
  // protocol keeps its own row so cloud rows never fork or merge across
  // protocols. This field only makes the relationship visible, so the emptier
  // twin can point at the richer one instead of looking like a second device.
  // Absent whenever the boat could not establish a bus identity.
  busIdentityFingerprint?: string;
  talkerId?: string;
  sourceLabel?: string;
  manufacturer?: { code: number | null; name: string | null };
  deviceClass?: { code: number; name: string | null };
  deviceFunction?: { code: number; name: string | null };
  productInfo?: {
    productCode: number | null;
    modelId: string | null;
    softwareVersion: string | null;
    modelVersion: string | null;
    serialCode: string | null;
  };
  configurationInfo?: {
    installationDescription1: string | null;
    installationDescription2: string | null;
    manufacturerInformation: string | null;
  };
  deviceLabel?: string;
  // Per-channel labels the device reports about itself (Maretron RIM100/SIM100
  // PGN 130818 today). Already 1-based and sentinel-filtered by the boat — the
  // cloud, this app, and the API never re-derive channel numbering. Absent for
  // non-Maretron / unlabeled devices, which is nearly every device on a boat.
  deviceChannels?: Array<{ channel: number; label: string; displayLabel?: string }>;
  transmitPgns?: number[];
  receivePgns?: number[];
  // Admin-published manuals for this device's model (ADR 0112); absent when none.
  manuals?: UserManual[];
  firstSeen: string;
  lastSeen: string;
}

export interface BoatFreshness {
  /**
   * ISO of the last `device_liveness` frame the cloud accepted.
   * Distinct from `lastContactedAt`, which fires on every WS upgrade —
   * `lastReportedAt` means the boat delivered an application payload
   * the cloud applied. NULL until the boat has sent its first liveness.
   */
  lastReportedAt: string | null;
  /** ISO of the most recent WS upgrade. NULL on a never-connected boat. */
  lastContactedAt: string | null;
}

export type BoatDevicesState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      devices: VesselDevice[];
      boat: BoatFreshness;
      /**
       * The `sourceId` of the boat's primary gateway per its latest source
       * analysis (`accept.preferredN2kInOutSourceId`), or null when no analysis
       * has named one. The Devices screen opens its gateway filter on it.
       */
      primarySourceId: string | null;
    }
  | { kind: 'error'; message: string };

export interface UseBoatDevices extends Record<string, unknown> {
  state: BoatDevicesState;
  refresh: () => void;
}

interface DevicesResponse {
  devices: VesselDevice[];
  boat?: BoatFreshness;
  // Additive (older clouds omit it) — absent behaves like "no primary known".
  primarySourceId?: string | null;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

export function useBoatDevices(boatId: string): UseBoatDevices {
  const [state, setState] = useState<BoatDevicesState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const lastRequestedBoatId = useRef<string | null>(null);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // GET the inventory + boat freshness from REST. Re-runs on tick / boatId.
  useEffect(() => {
    if (boatId === '') return;
    let cancelled = false;
    const boatChanged = lastRequestedBoatId.current !== boatId;
    lastRequestedBoatId.current = boatId;
    setState((prev) => (boatChanged || prev.kind !== 'ready' ? { kind: 'loading' } : prev));
    void (async () => {
      try {
        const data = await api<DevicesResponse>(`/v1/boats/${boatId}/devices`);
        if (!cancelled) {
          setState({
            kind: 'ready',
            devices: data.devices,
            boat: data.boat ?? { lastReportedAt: null, lastContactedAt: null },
            primarySourceId: data.primarySourceId ?? null,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as { status?: number; message?: string };
        const message =
          e.status === 403
            ? 'You do not have access to this boat.'
            : e.status === 401
              ? 'Please sign in again.'
              : (e.message ?? 'Failed to load devices.');
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick]);

  // Open a user-WS and subscribe to `boat.<id>.devices`. Every
  // notification (no payload) just bumps `tick` so the effect above
  // re-runs the GET. Auto-reconnect with bounded backoff. Disabled when
  // there's no boatId yet.
  useEffect(() => {
    if (boatId === '' || typeof WebSocket === 'undefined') return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = backoffMs;
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect(): void {
      if (cancelled) return;
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        try {
          ws.send(JSON.stringify({ type: 'subscribe', topic: `boat.${boatId}.devices` }));
          // Re-read the authoritative device list now that the subscription is
          // attached: any inventory_changed the boat published in the gap between
          // the mount GET and this subscription had no replay, so without this the
          // Devices grid can sit on the empty "still reading" floor until a manual
          // reload — even when the boat already reported dozens of devices. Cheap:
          // the GET keeps the current view when already `ready` (no flash), and it
          // self-heals on every reconnect. Mirrors the setup-settle re-read.
          setTick((t) => t + 1);
        } catch {
          // socket gone; onclose will retry
        }
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        try {
          const frame = JSON.parse(ev.data) as { type?: string };
          if (frame.type === 'devices') {
            setTick((t) => t + 1);
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onclose handles reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
  }, [boatId]);

  return { state, refresh };
}
